import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, X } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";

export const clips = [
  { src: "/video/reel-v5.mp4", poster: "/video/reel-v5-cover.jpg", preview: "/video/reel-v5-preview.mp4", focus: "38% 30%", label: "CROOKS S2 — The Garden" },
  { src: "/video/reel-v6.mp4", poster: "/video/reel-v6-cover.jpg", preview: "/video/reel-v6-preview.mp4", focus: "50% 40%", label: "CROOKS S2 — The Club" },

  { src: "/video/reel-v3.mp4", poster: "/video/reel-v3-cover.jpg", preview: "/video/reel-v3-preview.mp4", focus: "45% 45%", label: "Achara — Close-up" },
  { src: "/video/reel-v2.mp4", poster: "/video/reel-v2-cover.jpg", preview: "/video/reel-v2-preview.mp4", focus: "40% 40%", label: "CROOKS S2 — Scene" },
  { src: "/video/reel-v4.mp4", poster: "/video/reel-v4-cover.jpg", preview: "/video/reel-v4-preview.mp4", focus: "42% 40%", label: "CROOKS S2 — Standoff" },
  { src: "/video/reel-v1.mp4", poster: "/video/reel-v1-cover.jpg", preview: "/video/reel-v1-preview.mp4", focus: "55% 40%", label: "CROOKS S2 — Trailer cut" },
];

export const ReelModal = ({ start = 0, onClose, items = clips, title = "Showreel" }) => {
  const [i, setI] = useState(start);
  useEffect(() => {
    window.__lenis?.stop();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { window.__lenis?.start(); window.removeEventListener("keydown", onKey); };
  }, [onClose]);
  const c = items[i];
  return (
    <motion.div data-testid="showreel-modal" className="fixed inset-0 z-[80] bg-[#171411]/95 flex items-center justify-center p-4 sm:p-8"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 text-white">
          <Eyebrow className="!text-[var(--gold-soft)]">{title} — {c.label}</Eyebrow>
          <button data-testid="showreel-close-btn" onClick={onClose} className="p-2 text-white/80 hover:text-white" aria-label="Close"><X /></button>
        </div>
        <div className="bg-black aspect-[4/5] sm:aspect-video max-h-[70vh] w-full">
          <video key={c.src} data-testid="showreel-video" src={c.src} poster={c.poster} controls autoPlay playsInline
            onEnded={() => setI((i + 1) % items.length)} className="h-full w-full object-contain" />
        </div>
        <div className="mt-4 grid gap-2 sm:gap-3" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
          {items.map((x, k) => (
            <button key={x.src} data-testid={`showreel-clip-${k}`} onClick={() => setI(k)}
              className={`relative aspect-video overflow-hidden border transition-colors ${k === i ? "border-[var(--gold-soft)]" : "border-white/10 opacity-60 hover:opacity-100"}`}>
              <img src={x.poster} alt={x.label} className="h-full w-full object-cover" />
              <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] sm:text-[11px] uppercase tracking-[0.15em] px-2 py-1 text-left truncate">{x.label}</span>
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const ReelCard = ({ c, k, testid, onOpen }) => {
  const ref = useRef(null);
  const [live, setLive] = useState(false);
  useEffect(() => {
    const v = ref.current;
    if (!v || !c.preview || window.matchMedia("(hover: hover)").matches) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { v.play().then(() => setLive(true)).catch(() => {}); }
      else { v.pause(); setLive(false); }
    }, { threshold: 0.6 });
    io.observe(v);
    return () => io.disconnect();
  }, [c.preview]);
  const start = () => { const v = ref.current; if (v) v.play().then(() => setLive(true)).catch(() => {}); };
  const stop = () => { const v = ref.current; if (v) { v.pause(); v.currentTime = 0; } setLive(false); };
  return (
    <button data-testid={`${testid}-card-${k}`} onClick={() => onOpen(k)}
      onMouseEnter={start} onMouseLeave={stop}
      className="group relative block w-full aspect-[4/5] overflow-hidden bg-black text-left">
      <img src={c.poster} alt={c.label} className="absolute inset-0 h-full w-full object-cover group-hover:scale-[1.04] transition-transform duration-[1400ms] ease-out" style={{ objectPosition: c.focus || "50% 50%" }} />
      {c.preview && (
        <video ref={ref} data-testid={`${testid}-preview-${k}`} src={c.preview} muted loop playsInline preload="metadata"
          className={`absolute inset-0 h-full w-full object-cover scale-[1.04] transition-opacity duration-700 ${live ? "opacity-100" : "opacity-0"}`}
          style={{ objectPosition: c.previewFocus || c.focus || "50% 50%" }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border border-white/70 text-white flex items-center justify-center group-hover:bg-white group-hover:text-black transition-colors duration-500">
        <Play size={20} className="ml-1" />
      </span>
      <span className="absolute bottom-5 left-5 text-white text-[12px] uppercase tracking-[0.2em]">{c.label}</span>
    </button>
  );
};

export const ReelStrip = ({ onOpen, items = clips, title = "Showreel", testid = "showreel", cols = "sm:grid-cols-2 lg:grid-cols-4" }) => (
  <div data-testid={`${testid}-strip`} className="mt-20">
    <Reveal className="mb-8"><Eyebrow>{title}</Eyebrow></Reveal>
    <div className={`grid grid-cols-1 ${cols} gap-4 sm:gap-5`}>
      {items.map((c, k) => (
        <Reveal key={c.src} delay={k * 0.08}>
          <ReelCard c={c} k={k} testid={testid} onOpen={onOpen} />
        </Reveal>
      ))}
    </div>
  </div>
);
